import { Layers } from 'lucide-react';
import { useState } from 'react';
import { AudienceResponseDTO } from 'src/modules/audiences/infra/dtos';
import { Tier, TierFormValues } from 'src/modules/tiers/domain/types';
import { useCreateTierMutation } from 'src/modules/tiers/hooks/useCreateTierMutation';
import { useUpdateTierMutation } from 'src/modules/tiers/hooks/useUpdateTierMutation';

import TemporaryDrawer from '@/components/TemporaryDrawer';

import TierForm from './TierForm';

export interface TierFormDrawerProps {
  open: boolean;
  onClose: () => void;
  editingTier: Tier | null;
  audiences: AudienceResponseDTO[];
  defaultRank: number;
}

export default function TierFormDrawer({ open, onClose, editingTier, audiences, defaultRank }: TierFormDrawerProps) {
  const isEdit = editingTier !== null;
  const [isDirty, setIsDirty] = useState(false);

  const createMutation = useCreateTierMutation({ onSuccess: onClose });
  const updateMutation = useUpdateTierMutation(editingTier?.id ?? '', { onSuccess: onClose });

  const activeMutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: TierFormValues) => {
    activeMutation.mutate(values);
  };

  return (
    <TemporaryDrawer
      open={open}
      onClose={onClose}
      icon={<Layers />}
      title={isEdit ? 'Edit Tier' : 'Create Tier'}
      subtitle={
        isEdit
          ? 'Update the tier name, rank, or linked audience.'
          : 'Create a new tier with a name, rank, and optional audience.'
      }
      shouldConfirmClose={isDirty}
    >
      <TierForm
        editingTier={editingTier}
        audiences={audiences}
        defaultRank={defaultRank}
        isPending={activeMutation.isPending}
        onCancel={onClose}
        onSubmit={handleSubmit}
        onDirtyChange={setIsDirty}
      />
    </TemporaryDrawer>
  );
}
